import { House } from 'lucide-react';
import {
  homeBuyPence,
  housesOf,
  moveInPence,
  formatPence,
  type HomeKind,
  type HousesContent,
  type HousesRules,
  type RentalRules,
  rentalsOf,
} from '@london-life/contracts';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Switch } from '@london-life/ui/components/switch';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { useAdmin } from '../../lib/admin-context';
import { useContent } from './use-content';
import { ContentBar } from './versions';
import { Field, NumberField, idFor, type NumberSpec } from './form';

type RuleNumbers = Exclude<keyof HousesRules, 'extendHours' | 'fallback'>;
const RULES: NumberSpec<RuleNumbers>[] = [
  { key: 'upfrontWeeks', label: 'Weeks of rent up front to move in', unit: 'number', min: 0, max: 12, whole: true },
  { key: 'agentWeeks', label: "Agent's fee (weeks of rent)", unit: 'number', min: 0, max: 12 },
  { key: 'buyWeeks', label: 'Buying costs this many weeks of rent', unit: 'number', min: 1, max: 520, whole: true },
  { key: 'sellShare', label: 'Sold back for (of what was paid)', unit: 'percent', min: 0, max: 1 },
  { key: 'extendMetres', label: 'Each extension adds (metres each way)', unit: 'number', min: 0.5, max: 4 },
  { key: 'extendShare', label: 'Extension price (of the buy price, per floor added)', unit: 'percent', min: 0, max: 2 },
];
type LetNumbers = Exclude<keyof RentalRules, 'visible' | 'tierShare'>;
const LETS: NumberSpec<LetNumbers>[] = [
  { key: 'maxProperties', label: 'Most homes to let a player', unit: 'number', min: 1, max: 50, whole: true },
  { key: 'priceWeeks', label: 'Buying to let costs weeks of rent', unit: 'number', min: 1, max: 520 },
  { key: 'sellShare', label: 'Sold back for (of what was paid)', unit: 'percent', min: 0, max: 1 },
  { key: 'bandLow', label: 'Lowest rent asked (of the going rate)', unit: 'percent', min: 0.1, max: 1 },
  { key: 'bandHigh', label: 'Highest rent asked (of the going rate)', unit: 'percent', min: 1, max: 5 },
  { key: 'fullAt', label: 'Always let at or below (of the going rate)', unit: 'percent', min: 0.1, max: 1 },
  { key: 'atGoing', label: 'Evenings let at the going rate', unit: 'percent', min: 0, max: 1 },
  { key: 'atHigh', label: 'Evenings let at the highest rent', unit: 'percent', min: 0, max: 1 },
  { key: 'agentShare', label: "Agent's fee on the rent", unit: 'percent', min: 0, max: 0.5 },
  { key: 'catchUp', label: 'Evenings caught up at most', unit: 'number', min: 1, max: 60, whole: true },
  { key: 'buysPerDay', label: 'Homes bought to let a day', unit: 'number', min: 1, max: 100, whole: true },
];
type KindNumbers = 'rentPenceWeekly' | 'guests' | 'extensions';
const KIND: NumberSpec<KindNumbers>[] = [
  { key: 'rentPenceWeekly', label: 'Rent a week', unit: 'pounds', min: 0, max: 100_000_000 },
  { key: 'guests', label: 'Guests at once', unit: 'number', min: 1, max: 30, whole: true },
  { key: 'extensions', label: 'Extensions once owned', unit: 'number', min: 0, max: 4, whole: true },
];

// Houses (as Lagos Life's 🔑 Houses): the homes players live in and can move to, their rent and room, and the
// rules for moving in, buying, selling back and extending. Part of the content draft, published with it.
export function HousesPage() {
  const { can } = useAdmin();
  const editor = useContent();
  const working = editor.working;
  const readOnly = !can('content.write');
  const houses = housesOf(working ?? {});
  const change = (fn: (draft: HousesContent) => void) =>
    editor.edit((content) => {
      content.houses ??= structuredClone(housesOf(content));
      fn(content.houses);
    });
  const kind = (id: string, fn: (item: HomeKind) => void) =>
    change((draft) => {
      const item = draft.kinds.find((entry) => entry.id === id);
      if (item) fn(item);
    });
  return (
    <section>
      <PageHeader
        title="Houses"
        description="The homes players live in (as Lagos Life's Houses): rent, room size, what buying and extending cost. Changes are a draft until they are published."
      />
      <ContentBar editor={editor} />
      {editor.failure && <ErrorNotice error={editor.failure} onRetry={() => void editor.reload()} />}
      {!working && !editor.failure && <p className="admin-muted">Loading…</p>}
      {working && (
        <div className="content-editor">
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="The rules">
            <h2 className="content-subtitle">
              <House size={16} aria-hidden="true" /> The rules
            </h2>
            <div className="content-grid">
              {RULES.map((spec) => (
                <NumberField
                  key={spec.key}
                  id={`houses-${spec.key}`}
                  spec={spec}
                  value={houses.rules[spec.key]}
                  onChange={(value) => change((draft) => void (draft.rules[spec.key] = value))}
                />
              ))}
              <Field id="houses-fallback" label="Evicted players are housed in">
                <select
                  id="houses-fallback"
                  className="content-select"
                  value={houses.rules.fallback}
                  onChange={(e) => change((draft) => void (draft.rules.fallback = e.target.value))}
                >
                  {houses.kinds.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Buy to let">
            <h2 className="content-subtitle">Buy to let</h2>
            <label className="designer-check">
              <Switch
                checked={rentalsOf(houses).visible}
                aria-label="Buying to let open"
                onCheckedChange={(on) => change((draft) => void (draft.rentals = { ...rentalsOf(draft), visible: on }))}
              />{' '}
              Players can buy homes to let
            </label>
            <div className="content-grid">
              {LETS.map((spec) => (
                <NumberField
                  key={spec.key}
                  id={`lets-${spec.key}`}
                  spec={spec}
                  value={rentalsOf(houses)[spec.key]}
                  onChange={(value) =>
                    change((draft) => void (draft.rentals = { ...rentalsOf(draft), [spec.key]: value }))
                  }
                />
              ))}
            </div>
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Homes">
            <h2 className="content-subtitle">Homes</h2>
            <p className="admin-muted">
              Hidden homes leave the market; players living in one keep it. The three starting homes cannot be removed.
            </p>
            {houses.kinds.map((item) => (
              <div className="content-grid content-row" key={item.id} data-testid={`home-${item.id}`}>
                <Field id={`home-${item.id}-label`} label="Name">
                  <Input
                    id={`home-${item.id}-label`}
                    value={item.label}
                    maxLength={40}
                    onChange={(e) => kind(item.id, (entry) => void (entry.label = e.target.value))}
                  />
                </Field>
                <Field id={`home-${item.id}-area`} label="Area">
                  <Input
                    id={`home-${item.id}-area`}
                    value={item.area}
                    maxLength={40}
                    onChange={(e) => kind(item.id, (entry) => void (entry.area = e.target.value))}
                  />
                </Field>
                <Field id={`home-${item.id}-icon`} label="Icon">
                  <Input
                    id={`home-${item.id}-icon`}
                    value={item.icon}
                    maxLength={16}
                    onChange={(e) => kind(item.id, (entry) => void (entry.icon = e.target.value))}
                  />
                </Field>
                <Field id={`home-${item.id}-description`} label="What it is like">
                  <Input
                    id={`home-${item.id}-description`}
                    value={item.description}
                    maxLength={160}
                    onChange={(e) => kind(item.id, (entry) => void (entry.description = e.target.value))}
                  />
                </Field>
                {KIND.map((spec) => (
                  <NumberField
                    key={spec.key}
                    id={`home-${item.id}-${spec.key}`}
                    spec={spec}
                    value={item[spec.key]}
                    onChange={(value) => kind(item.id, (entry) => void (entry[spec.key] = value))}
                  />
                ))}
                {(['w', 'd'] as const).map((side) => (
                  <NumberField
                    key={side}
                    id={`home-${item.id}-${side}`}
                    spec={{
                      key: side,
                      label: side === 'w' ? 'Width (m)' : 'Depth (m)',
                      unit: 'number',
                      min: 3,
                      max: 16,
                    }}
                    value={item.floor[side]}
                    onChange={(value) => kind(item.id, (entry) => void (entry.floor[side] = value))}
                  />
                ))}
                {(['wallColour', 'floorColour'] as const).map((part) => (
                  <Field key={part} id={`home-${item.id}-${part}`} label={part === 'wallColour' ? 'Walls' : 'Floor'}>
                    <Input
                      id={`home-${item.id}-${part}`}
                      type="color"
                      value={item[part]}
                      onChange={(e) => kind(item.id, (entry) => void (entry[part] = e.target.value))}
                    />
                  </Field>
                ))}
                <p className="admin-muted">
                  Move in {formatPence(moveInPence(item, houses.rules))} · buy{' '}
                  {formatPence(homeBuyPence(item, houses.rules))}
                </p>
                <label className="designer-check">
                  <Switch
                    checked={item.visible}
                    aria-label={`${item.label} on the market`}
                    onCheckedChange={(on) => kind(item.id, (entry) => void (entry.visible = on))}
                  />{' '}
                  On the market
                </label>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                change((draft) => {
                  const template = draft.kinds.at(-1)!;
                  draft.kinds.push({
                    ...structuredClone(template),
                    id: idFor('New home', new Set(draft.kinds.map((entry) => entry.id)), 'home', 30),
                    label: 'New home',
                    starter: [],
                    visible: false,
                  });
                })
              }
            >
              Add a home
            </Button>
          </fieldset>
        </div>
      )}
    </section>
  );
}
